import axios from 'axios';

import appConfig from '@/config';
import alert from '@/modules/account/common/alert';
import LocalStorage from '@/services/LocalStorage';
import Logger from '@/services/Logger';
import i18n from '@/i18n';

class Http {
  constructor(path = '', config, server = 'api') {
    this.path = path;
    this.supressDefault = false;
    this.treatment = null;
    const fromConfigFile = appConfig[server];

    fromConfigFile.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    this.axios = axios.create({ ...fromConfigFile, ...config });
    this.axios.interceptors.request.use((axiosConfig) => {
      const organization = LocalStorage.get('organization');
      if (
        !axiosConfig.headers?.['X-Organization'] &&
        organization &&
        organization !== 'undefined'
      ) {
        // eslint-disable-next-line no-param-reassign
        axiosConfig.headers['X-Organization'] = organization;
      }
      return axiosConfig;
    });

    this.axios.interceptors.response.use(null, (error) =>
      this.customInterceptor(error)
    );
  }

  customInterceptor(error) {
    if (this.treatment) {
      this.treatment(error);
    } else {
      Http.defaultErrorFlowHandler(error);
    }

    if (!this.supressDefault) {
      Http.handleErrorMessage(error);
    }
    throw error;
  }

  static defaultErrorFlowHandler(error) {
    if (
      error.response?.status === 401 ||
      (error.response?.status === 403 &&
        error.response?.data.message === 'You must complete your registration.')
    ) {
      Logger.consoleError('Unauthorized error user registration', error);
      window.location.assign(
        `${appConfig.loginPath}?continue=${window.location}`
      );
      return;
    }
    if (
      error.response?.status === 403 &&
      error.response?.data.message === 'Your email address is not verified.'
    ) {
      Logger.consoleError('Unauthorized error email not verified:', error);
      window.location.assign(
        `${appConfig.verifyEmailPath}?continue=${window.location}`
      );
      return;
    }
  }

  static handleErrorMessage(error) {
    const status = error.response?.status;
    const code = error?.code;
    const message = error.response?.data?.message;
    const entity = error.response?.data?.errors;
    let fieldsError = entity
      ? i18n.t('http.invalidFields') +
        Object.entries(entity)
          .map(
            ([key, messages]) =>
              `${key.split('.').pop()}: ${messages.join(', ')}`
          )
          .join('; ')
      : message;
    let errorMessage = '';

    const isTimeout =
      code === 'ECONNABORTED' ||
      code === 'ETIMEDOUT' ||
      message?.toLowerCase().includes('timed out') ||
      message?.toLowerCase().includes('timeout');

    const isNetworkError =
      code === 'ERR_NETWORK' || (error?.request && !error?.response);

    switch (status) {
      case 422:
        if (
          message === 'Credenciais inválidas' ||
          message === 'Invalid credentials'
        ) {
          Http.invalidCredentialsMessageHandler(error);
          break;
        }
        errorMessage = i18n.t('http.unprocessableEntityError', {
          entity: fieldsError,
        });
        Http.defaultErrorMessageHandler(errorMessage);
        break;
      case 402:
        Logger.consoleError('Payment error:', error);
        break;
      case 403:
        Logger.consoleError('Forbidden access:', error);
        break;
      case 504:
        Logger.consoleError('Gateway timeout:', error);
        errorMessage = i18n.t('http.serverGatewayTimeout');
        Http.defaultErrorMessageHandler(errorMessage);
        break;
      default:
        if (isTimeout) {
          Logger.consoleError('Timeout error', error);
          errorMessage = i18n.t('http.timeoutError', {
            message: message,
          });
          Http.defaultErrorMessageHandler(errorMessage);
          break;
        }
        if (isNetworkError) {
          Logger.consoleError('Connection error', error);
          errorMessage = i18n.t('http.connectionError', {
            message: message,
          });
          Http.defaultErrorMessageHandler(errorMessage);
          break;
        }
        if (status !== 500) {
          Logger.consoleError('Client error', error);
          errorMessage = i18n.t('http.clientError', {
            message: message,
          });
          Http.defaultErrorMessageHandler(errorMessage);
          break;
        }
        Logger.consoleError('Server error:', error);
        Http.defaultErrorMessageHandler(message);
    }
  }

  static defaultErrorMessageHandler(message = '') {
    message = message?.trim();
    const errorMessage = message || i18n.t('http.defaultError');
    alert.fireAlert(errorMessage, {
      classes: 'alert-danger',
      styles:
        'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
      tag: 'httpAlert',
      icon: 'triangle-exclamation',
    });
  }

  static invalidCredentialsMessageHandler(error) {
    error.isInvalidCredentials = true;
    error.message = error.response?.data?.message;
  }

  setErrorHandler(treatment, supressDefault) {
    if (supressDefault !== undefined) {
      this.supressDefault = supressDefault;
    }
    this.treatment = treatment;
    return this;
  }

  get(id = '', params = '', path = '', config) {
    let queryParams = '';
    if (typeof params === 'object') {
      queryParams = `?${new URLSearchParams(params).toString()}`;
    }
    return this.axios.get(
      id
        ? `${this.path}${path}/${id}${queryParams}`
        : `${this.path}${path}${queryParams}`,
      config
    );
  }

  post(data, path, config) {
    if (!config) {
      // eslint-disable-next-line no-param-reassign
      config = {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
    }
    return this.axios.post(path || this.path, data, config);
  }

  put(id, data, path, config) {
    return this.axios.put(
      id ? `${path || this.path}/${id}` : `${this.path}${path}`,
      data,
      config
    );
  }

  delete(id, params, path, config) {
    let queryParams = '';
    if (typeof params === 'object') {
      queryParams = `?${new URLSearchParams(params).toString()}`;
    }
    return this.axios.delete(
      id ? `${path || this.path}/${id}${queryParams}` : `${this.path}${path}`,
      config
    );
  }

  patch(id, data, path, config) {
    if (id) {
      return this.axios.patch(`${path || this.path}/${id}`, data, config);
    }
    return this.axios.patch(`${path || this.path}`, data, config);
  }

  getWithOrganization(
    id = '',
    params = '',
    path = '',
    organizationId,
    config = {}
  ) {
    return this.get(id, params, path, {
      ...config,
      headers: {
        ...(config.headers || {}),
        'X-Organization': organizationId,
      },
    });
  }

  async getFileObjectFromBase64(base64) {
    const response = await fetch(base64);
    const blob = await response.blob();
    return new File([blob], 'image', { type: blob.type });
  }
}

export default Http;
